import React from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { 
  Calendar, 
  Clock, 
  Tag,
  ExternalLink,
  ChevronRight,
  MapPin,
  Phone,
  Mail,
  Globe
} from 'lucide-react'
import { emailUtils } from '../../utils/contactUtils'

interface NewsArticle {
  id: string
  title: string
  subtitle: string
  category: 'company' | 'industry' | 'technology' | 'education' | 'global'
  date: string
  content: string
  highlights: string[]
  source?: string
  location?: string
  readTime: string
}

// Article metadata with categories, dates, and locations
const articleMetadata: Record<string, {
  category: 'company' | 'industry' | 'technology' | 'education' | 'global'
  date: string
  location?: string
  readTime: string
  source?: string
}> = {
  'westernLibraryForum2025': {
    category: 'education',
    date: '2025-06-04',
    location: 'Chengdu, Sichuan',
    readTime: '5 min read'
  },
  'intelligentBookSelection2025': {
    category: 'technology',
    date: '2025-03-11',
    location: 'Guangdong Province',
    readTime: '4 min read'
  },
  'aiEmpoweredReading2025': {
    category: 'industry',
    date: '2025-03-03',
    readTime: '6 min read'
  },
  'deepseekLibraryRevolution2025': {
    category: 'technology',
    date: '2025-02-24',
    location: 'Xuzhou, Jiangsu',
    readTime: '4 min read'
  },
  'shenzhenGlobalCulture2025': {
    category: 'industry',
    date: '2025-02-17',
    location: 'Shenzhen, Guangdong',
    readTime: '7 min read'
  },
  'smartReadingUnlimited2025': {
    category: 'industry',
    date: '2025-02-10',
    readTime: '5 min read'
  },
  'singaporeNationalLibraryAi': {
    category: 'global',
    date: '2025-05-15',
    location: 'Singapore',
    readTime: '6 min read',
    source: 'Singapore National Library Board'
  },
  'ukCambridgeQuantumLibrary': {
    category: 'global',
    date: '2025-04-22',
    location: 'Cambridge, United Kingdom',
    readTime: '5 min read',
    source: 'Cambridge University Library'
  },
  'finlandHelsinkiCarbonNeutral': {
    category: 'global',
    date: '2025-04-08',
    location: 'Helsinki, Finland',
    readTime: '4 min read',
    source: 'Helsinki City Library'
  },
  'usaMitBlockchainLibrary': {
    category: 'global',
    date: '2025-03-30',
    location: 'Cambridge, Massachusetts, USA',
    readTime: '6 min read',
    source: 'MIT Libraries'
  },
  'australiaMelbourneVrLibrary': {
    category: 'global',
    date: '2025-03-18',
    location: 'Melbourne, Australia',
    readTime: '5 min read',
    source: 'State Library Victoria'
  }
}

const News: React.FC = () => {
  const { t } = useTranslation()

  // Generate news articles from translation data
  const newsArticles: NewsArticle[] = Object.keys(articleMetadata).map(articleId => {
    const metadata = articleMetadata[articleId]
    const highlights = t(`pages.news.articles.${articleId}.highlights`, { returnObjects: true, defaultValue: [] })
    return {
      id: articleId,
      title: t(`pages.news.articles.${articleId}.title`),
      subtitle: t(`pages.news.articles.${articleId}.subtitle`),
      content: t(`pages.news.articles.${articleId}.content`),
      highlights: Array.isArray(highlights) ? highlights : [],
      category: metadata.category,
      date: metadata.date,
      location: metadata.location,
      readTime: metadata.readTime,
      source: metadata.source
    }
  })

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    })
  }

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'company': return 'bg-blue-100 text-blue-800'
      case 'industry': return 'bg-green-100 text-green-800'
      case 'technology': return 'bg-purple-100 text-purple-800'
      case 'education': return 'bg-orange-100 text-orange-800'
      case 'global': return 'bg-indigo-100 text-indigo-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white pt-24">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-blue-600 to-blue-700 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-4xl md:text-5xl font-bold mb-6"
            >
              {t('pages.news.hero.title')}
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xl md:text-2xl text-blue-100 mb-8"
            >
              {t('pages.news.hero.subtitle')}
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-lg text-blue-200 max-w-3xl mx-auto"
            >
              {t('pages.news.hero.description')}
            </motion.p>
          </div>
        </div>
      </section>



      {/* News Articles */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {newsArticles.map((article, index) => (
              <motion.article
                key={article.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"
              >
                {/* Article Header */}
                <div className="p-6 border-b border-gray-100">
                  <div className="flex items-center justify-between mb-4">
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(article.category)}`}>
                      {t(`pages.news.filters.${article.category}`)}
                    </span>
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <span className="flex items-center space-x-1">
                        <Calendar size={14} />
                        <span>{formatDate(article.date)}</span>
                      </span>
                      <span className="flex items-center space-x-1">
                        <Clock size={14} />
                        <span>{article.readTime}</span>
                      </span>
                    </div>
                  </div>
                  
                  <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mb-2 line-clamp-2">
                    {article.title}
                  </h2>
                  
                  <p className="text-blue-600 font-medium mb-3">
                    {article.subtitle}
                  </p>
                  
                  {article.location && (
                    <div className="flex items-center space-x-2 text-sm text-gray-500 mb-3">
                      <MapPin size={14} />
                      <span>{article.location}</span>
                      {article.source && (
                        <>
                          <span>•</span>
                          <span>{t('pages.news.source')}: {article.source}</span>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* Article Content */}
                <div className="p-6">
                  <p className="text-gray-700 leading-relaxed mb-6">
                    {article.content}
                  </p>

                  {/* Highlights */}
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <Tag className="w-5 h-5 mr-2 text-blue-600" />
                      {t('common.keyHighlights')}
                    </h3>
                    <div className="space-y-2">
                      {Array.isArray(article.highlights) && article.highlights.slice(0, 4).map((highlight, idx) => (
                        <div key={idx} className="flex items-start space-x-3">
                          <ChevronRight className="w-4 h-4 text-blue-500 mt-1 flex-shrink-0" />
                          <span className="text-gray-600 text-sm leading-relaxed">{highlight}</span>
                        </div>
                      ))}
                      {Array.isArray(article.highlights) && article.highlights.length > 4 && (
                        <div className="text-sm text-blue-600 font-medium">
                          +{article.highlights.length - 4} {t('common.moreHighlights')}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Read More Button */}
                  <button 
                    onClick={() => emailUtils.openNewsReadMoreEmail(t, article.title, article.id)}
                    className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors duration-200 flex items-center justify-center space-x-2"
                  >
                    <span>{t('pages.news.readMore')}</span>
                    <ExternalLink size={16} />
                  </button>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Mail className="w-16 h-16 mx-auto mb-6 text-blue-200" />
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                {t('common.cta.readyToTransform')}
              </h2>
              <p className="text-xl text-blue-100 mb-8">
                {t('common.cta.contactDescription')}
              </p>
              <button 
                onClick={() => emailUtils.openConsultationEmail(t)}
                className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-blue-50 transition-colors duration-200 inline-flex items-center space-x-2"
              >
                <Mail size={20} />
                <span>{t('common.cta.getFreeConsultation')}</span>
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <h3 className="text-2xl font-bold mb-4">{t('pages.news.contact.shareNews')}</h3>
            <p className="text-gray-300 mb-6">
              {t('pages.news.contact.contactEditorial')}
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center text-sm">
              <div className="flex items-center space-x-2">
                <Phone size={16} />
                <span>+86 400-996-7738</span>
              </div>
              <div className="flex items-center space-x-2">
                <Mail size={16} />
                <button 
                  onClick={() => emailUtils.openDirectEmail('rfidlib@libmj.com')}
                  className="text-blue-600 hover:text-blue-800 underline cursor-pointer transition-colors duration-200"
                  title="发送邮件到 rfidlib@libmj.com"
                >
                  rfidlib@libmj.com
                </button>
              </div>
              <div className="flex items-center space-x-2">
                <Globe size={16} />
                <span>{t('pages.news.contact.globalCoverage')}</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default News 